/**
 * Interactive Map Component - Feel Umbria
 * ========================================
 * 
 * Real Mapbox GL JS implementation with:
 * - Umbria region boundary overlay (GeoJSON)
 * - Interactive zoom/pan
 * - Fly-to animations when locations change
 * - Marker clusters
 * - "Wow" effect during conversation
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { X, Navigation, Compass, Home, MapPin, Train, Calendar } from 'lucide-react';
import { Badge } from './ui/badge';

// Umbria region boundary GeoJSON
import umbriaBoundary from '../data/umbria_boundary.json';

// Mapbox access token
const MAPBOX_TOKEN = 'pk.eyJ1IjoiZGFuY2l0eSIsImEiOiJjbW1kNG5vcGkwYzhuMm9yMTZpenVhNTBhIn0.Qz4GCHaGma0zOypHBBeyzQ';
mapboxgl.accessToken = MAPBOX_TOKEN;

// Umbria center
const UMBRIA_CENTER = [12.57, 42.85];
const UMBRIA_BOUNDS = [
  [11.89, 42.36], // Southwest
  [13.27, 43.62]  // Northeast
];

// Macro areas with centers
const AREA_CENTERS = {
  "Valle Umbra": { lng: 12.70, lat: 42.88, zoom: 11 },
  "Perugino": { lng: 12.40, lat: 43.05, zoom: 10 },
  "Valnerina": { lng: 12.92, lat: 42.75, zoom: 11 },
  "Spoletino": { lng: 12.73, lat: 42.73, zoom: 11 },
  "Orvietano": { lng: 12.11, lat: 42.72, zoom: 11 },
  "Tuderte": { lng: 12.41, lat: 42.78, zoom: 11 },
  "Trasimeno": { lng: 12.10, lat: 43.10, zoom: 11 },
  "Ternano": { lng: 12.65, lat: 42.56, zoom: 11 },
  "Altotevere": { lng: 12.24, lat: 43.46, zoom: 11 },
  "Eugubino": { lng: 12.58, lat: 43.35, zoom: 11 },
};

// Known locations with coordinates
const LOCATION_COORDS = {
  "foligno": { lng: 12.71, lat: 42.95, type: "city" },
  "spoleto": { lng: 12.74, lat: 42.73, type: "city" },
  "perugia": { lng: 12.39, lat: 43.11, type: "city" },
  "assisi": { lng: 12.62, lat: 43.07, type: "city" },
  "orvieto": { lng: 12.11, lat: 42.72, type: "city" },
  "todi": { lng: 12.41, lat: 42.78, type: "city" },
  "norcia": { lng: 13.09, lat: 42.79, type: "city" },
  "gubbio": { lng: 12.58, lat: 43.35, type: "city" },
  "spello": { lng: 12.67, lat: 42.99, type: "city" },
  "montefalco": { lng: 12.65, lat: 42.89, type: "city" },
  "trevi": { lng: 12.75, lat: 42.88, type: "city" },
  "bevagna": { lng: 12.61, lat: 42.93, type: "city" },
  "cascia": { lng: 13.01, lat: 42.72, type: "city" },
  "castelluccio": { lng: 13.21, lat: 42.83, type: "village" },
  "città di castello": { lng: 12.24, lat: 43.46, type: "city" },
  "lago trasimeno": { lng: 12.10, lat: 43.10, type: "lake" },
  "castiglione del lago": { lng: 12.05, lat: 43.13, type: "city" },
  "passignano": { lng: 12.13, lat: 43.19, type: "city" },
  "città della pieve": { lng: 12.00, lat: 42.95, type: "city" },
  "deruta": { lng: 12.42, lat: 42.98, type: "city" },
  "narni": { lng: 12.52, lat: 42.52, type: "city" },
  "terni": { lng: 12.65, lat: 42.56, type: "city" },
  "cascata delle marmore": { lng: 12.71, lat: 42.55, type: "attraction" },
  "stazione foligno": { lng: 12.71, lat: 42.95, type: "station" },
  "stazione perugia": { lng: 12.39, lat: 43.10, type: "station" },
  "umbria jazz": { lng: 12.39, lat: 43.11, type: "event" },
};

export const InteractiveMap = ({
  isOpen,
  onClose,
  mapCommand = null,
  userBase = null,
  userArea = null,
  suggestedLocations = [],
  onLocationSelect = null,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [currentMode, setCurrentMode] = useState('exploration');

  // Initialize map
  useEffect(() => {
    if (!isOpen || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: UMBRIA_CENTER,
      zoom: 8,
      maxBounds: [
        [10.5, 41.5], // Wider bounds for context
        [14.5, 44.5]
      ],
      pitch: 20,
      bearing: 0,
    });

    map.on('load', () => {
      // Add Umbria boundary source
      map.addSource('umbria-boundary', {
        type: 'geojson',
        data: umbriaBoundary
      });

      // Add boundary fill (subtle)
      map.addLayer({
        id: 'umbria-fill',
        type: 'fill',
        source: 'umbria-boundary',
        paint: {
          'fill-color': '#84A98C',
          'fill-opacity': 0.08
        }
      });

      // Add boundary line (prominent)
      map.addLayer({
        id: 'umbria-border',
        type: 'line',
        source: 'umbria-boundary',
        paint: {
          'line-color': '#52796F',
          'line-width': 3,
          'line-opacity': 0.9
        }
      });

      // Add glow effect
      map.addLayer({
        id: 'umbria-glow',
        type: 'line',
        source: 'umbria-boundary',
        paint: {
          'line-color': '#84A98C',
          'line-width': 8,
          'line-opacity': 0.3,
          'line-blur': 3
        }
      }, 'umbria-border');

      // Fit to Umbria bounds with animation
      map.fitBounds(UMBRIA_BOUNDS, {
        padding: 40,
        duration: 1500,
        essential: true
      });

      setMapLoaded(true);
    });

    // Add navigation controls
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [isOpen]);

  // Clear existing markers
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
  }, []);

  // Add a marker to the map
  const addMarker = useCallback((location, options = {}) => {
    if (!mapRef.current || !mapLoaded) return;

    const {
      color = '#52796F',
      size = 'medium',
      icon = 'pin',
      label = '',
      pulse = false,
    } = options;

    // Create marker element
    const el = document.createElement('div');
    el.className = 'custom-marker';
    
    const sizeClass = size === 'large' ? 'w-12 h-12' : size === 'small' ? 'w-6 h-6' : 'w-9 h-9';
    const iconSize = size === 'large' ? 24 : size === 'small' ? 12 : 16;
    
    el.innerHTML = `
      <div class="relative">
        ${pulse ? `<div class="absolute inset-0 rounded-full animate-ping opacity-40" style="background-color: ${color}"></div>` : ''}
        <div class="${sizeClass} rounded-full flex items-center justify-center shadow-lg border-2 border-white" style="background-color: ${color}">
          ${icon === 'home' ? '<svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" class="w-5 h-5"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/></svg>' :
            icon === 'train' ? '<svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" class="w-5 h-5"><rect width="16" height="16" x="4" y="3" rx="2"/><path d="M4 11h16"/><path d="M12 3v8"/><path d="m8 19-2 3"/><path d="m18 22-2-3"/><path d="M8 15h0"/><path d="M16 15h0"/></svg>' :
            icon === 'calendar' ? '<svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" class="w-5 h-5"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>' :
            '<svg viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2" class="w-5 h-5"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3" fill="' + color + '"/></svg>'
          }
        </div>
        ${label ? `<div class="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap"><span class="text-xs font-semibold px-2 py-0.5 bg-white/95 rounded shadow-sm text-gray-800">${label}</span></div>` : ''}
      </div>
    `;

    const marker = new mapboxgl.Marker(el)
      .setLngLat([location.lng, location.lat])
      .addTo(mapRef.current);

    markersRef.current.push(marker);
    return marker;
  }, [mapLoaded]);

  // Fly to a location
  const flyTo = useCallback((location, zoom = 12) => {
    if (!mapRef.current || !mapLoaded) return;

    mapRef.current.flyTo({
      center: [location.lng, location.lat],
      zoom: zoom,
      duration: 2000,
      essential: true,
      pitch: 30,
    });
  }, [mapLoaded]);

  // Update map based on props changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    clearMarkers();

    // Handle mapCommand
    if (mapCommand) {
      const mode = mapCommand.mode || 'exploration';
      setCurrentMode(mode);

      // Add anchor marker (arrival point)
      if (mapCommand.anchor) {
        const anchorKey = mapCommand.anchor.toLowerCase();
        const coords = LOCATION_COORDS[anchorKey];
        if (coords) {
          addMarker(coords, {
            color: '#F59E0B',
            size: 'large',
            icon: coords.type === 'station' ? 'train' : coords.type === 'event' ? 'calendar' : 'pin',
            label: mapCommand.anchor,
            pulse: true
          });
          flyTo(coords, 12);
        }
      }

      // Add base marker
      if (mapCommand.base) {
        const baseKey = mapCommand.base.toLowerCase();
        const coords = LOCATION_COORDS[baseKey] || AREA_CENTERS[mapCommand.base];
        if (coords) {
          addMarker(coords, {
            color: '#10B981',
            size: 'large',
            icon: 'home',
            label: mapCommand.base,
            pulse: true
          });
          if (!mapCommand.anchor) {
            flyTo(coords, 11);
          }
        }
      }

      // Add stops
      if (mapCommand.stops && mapCommand.stops.length > 0) {
        mapCommand.stops.forEach((stop, idx) => {
          const stopKey = (stop.location || stop.name || '').toLowerCase();
          const coords = LOCATION_COORDS[stopKey];
          if (coords) {
            addMarker(coords, {
              color: '#3B82F6',
              size: 'medium',
              icon: 'pin',
              label: `${idx + 1}. ${stop.name || stop.location}`
            });
          }
        });
      }

      // Highlight areas (exploration mode)
      if (mapCommand.areas && mapCommand.areas.length > 0) {
        mapCommand.areas.forEach(area => {
          const coords = AREA_CENTERS[area];
          if (coords) {
            addMarker(coords, {
              color: '#8B5CF6',
              size: 'medium',
              icon: 'pin',
              label: area
            });
          }
        });
        
        // Fit to show all areas
        mapRef.current.fitBounds(UMBRIA_BOUNDS, {
          padding: 60,
          duration: 1500
        });
      }
      
      // Handle pulse location (mentioned in chat)
      if (mapCommand.pulse) {
        const pulseKey = mapCommand.pulse.toLowerCase();
        const coords = LOCATION_COORDS[pulseKey];
        if (coords) {
          addMarker(coords, {
            color: '#F59E0B', // Orange for attention
            size: 'large',
            icon: 'pin',
            label: mapCommand.pulse.charAt(0).toUpperCase() + mapCommand.pulse.slice(1),
            pulse: true
          });
          flyTo(coords, 12);
        }
      }
      
      // Handle multiple mentions (secondary markers)
      if (mapCommand.mentions && mapCommand.mentions.length > 1) {
        mapCommand.mentions.slice(1).forEach(loc => {
          const locKey = loc.toLowerCase();
          const coords = LOCATION_COORDS[locKey];
          if (coords) {
            addMarker(coords, {
              color: '#6B7280', // Gray for secondary
              size: 'medium',
              icon: 'pin',
              label: loc.charAt(0).toUpperCase() + loc.slice(1),
              pulse: false
            });
          }
        });
      }
    }

    // Handle userBase/userArea without mapCommand
    else if (userBase || userArea) {
      if (userBase) {
        const baseKey = userBase.toLowerCase();
        const coords = LOCATION_COORDS[baseKey];
        if (coords) {
          addMarker(coords, {
            color: '#10B981',
            size: 'large',
            icon: 'home',
            label: userBase,
            pulse: true
          });
          flyTo(coords, 12);
        }
      } else if (userArea && AREA_CENTERS[userArea]) {
        const coords = AREA_CENTERS[userArea];
        flyTo(coords, coords.zoom || 11);
      }
    }

    // Handle suggested locations
    if (suggestedLocations && suggestedLocations.length > 0) {
      suggestedLocations.forEach((loc, idx) => {
        const locKey = (loc.location || loc.name || '').toLowerCase();
        const coords = LOCATION_COORDS[locKey];
        if (coords) {
          addMarker(coords, {
            color: '#6366F1',
            size: 'small',
            icon: 'pin',
            label: loc.name || loc.location
          });
        }
      });
    }

  }, [mapCommand, userBase, userArea, suggestedLocations, mapLoaded, addMarker, clearMarkers, flyTo]);

  if (!isOpen) return null;

  const modeLabels = {
    exploration: 'Esplorazione',
    anchored: 'Dal tuo punto di arrivo',
    draft: 'Bozza itinerario',
    operational: 'Dettaglio',
    scenario: 'Due direzioni'
  };

  return (
    <div 
      className="fixed inset-y-0 right-0 w-full md:w-[460px] lg:w-[520px] z-50 animate-slide-in-right"
      data-testid="interactive-map-panel"
    >
      <div className="h-full bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-umbria-stone/30 bg-gradient-to-r from-umbria-olive/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-umbria-olive to-umbria-sage flex items-center justify-center shadow-md">
              <Navigation className="text-white" size={20} />
            </div>
            <div>
              <h3 className="font-serif font-semibold text-umbria-charcoal text-lg">
                Umbria
              </h3>
              <p className="text-xs text-umbria-muted flex items-center gap-1">
                <Compass size={12} />
                {modeLabels[currentMode] || 'Mappa'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-umbria-stone/30 flex items-center justify-center transition-all hover:rotate-90 duration-300"
            data-testid="map-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Info bar */}
        {mapCommand?.intro && (
          <div className="px-4 py-3 bg-amber-50/80 border-b border-amber-100/50">
            <p className="text-sm text-amber-900 italic leading-relaxed">
              "{mapCommand.intro}"
            </p>
          </div>
        )}

        {/* Legend */}
        <div className="px-4 py-2 bg-gray-50/80 border-b border-gray-100/50 flex flex-wrap gap-2">
          {mapCommand?.anchor && (
            <Badge variant="outline" className="gap-1 bg-amber-50 border-amber-200 text-amber-700">
              <Train size={12} />
              Arrivo
            </Badge>
          )}
          {(mapCommand?.base || userBase) && (
            <Badge variant="outline" className="gap-1 bg-green-50 border-green-200 text-green-700">
              <Home size={12} />
              Base
            </Badge>
          )}
          {mapCommand?.stops?.length > 0 && (
            <Badge variant="outline" className="gap-1 bg-blue-50 border-blue-200 text-blue-700">
              <MapPin size={12} />
              {mapCommand.stops.length} tappa{mapCommand.stops.length > 1 ? 'e' : ''}
            </Badge>
          )}
        </div>

        {/* Map container */}
        <div className="flex-1 relative">
          <div 
            ref={mapContainerRef} 
            className="absolute inset-0"
            style={{ width: '100%', height: '100%' }}
          />
          
          {!mapLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-umbria-paper">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-umbria-olive/30 border-t-umbria-olive rounded-full animate-spin mx-auto mb-4" />
                <p className="text-sm text-umbria-muted">Caricamento mappa...</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer with region info */}
        <div className="p-3 bg-gradient-to-r from-umbria-olive/5 to-transparent border-t border-umbria-stone/20">
          <p className="text-xs text-umbria-muted text-center">
            Regione Umbria - Il cuore verde d'Italia
          </p>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;
